import bootstrapper from '../bootstrapper';
import {
  flywireCheckoutEnabled, displayFlashErrorMessage, removeFlashErrorMessage, installmentsNumber,
} from './checkoutHelpers';
import { clientSideFailedPaymentPayload, createFailedPayment } from './createFailedPayment';

import updateFailedPayment from './updateFailedPayment';

const processingErrorMessage = 'An error occurred while processing your payment. Please try again';
let retryCount = 0;

const getFlywireLoader = () => document.getElementById('js-flywire-modal-loader');
const getFlywireData = () => {
  const flywireLoader = getFlywireLoader();
  return flywireLoader ? JSON.parse(flywireLoader.dataset.flywire) : null;
};
const getRetryButton = () => document.getElementsByClassName('js-flywire-retry-button')[0];

const showRetryButton = () => {
  const retryButton = getRetryButton();
  if (!retryButton) return;

  if (retryCount < 1) {
    retryButton.classList.remove('hidden');
    setTimeout(() => {
      retryButton.classList.remove('disabled');
    }, 2000);
    retryCount += 1;
  } else {
    retryButton.classList.remove('hidden');
  }
};

const hideRetryButton = () => {
  const retryButton = getRetryButton();
  if (!retryButton) return;

  if (!retryButton.classList.contains('hidden')) {
    retryButton.classList.add('hidden');
  }
};

const applyCssToCheckoutIframe = () => {
  const iframe = document.querySelector('iframe[data-testid="checkout-iframe"]');
  iframe.style.zIndex = 2147483647;
  iframe.style.border = '1px solid #e8e8e8';
};

const createFailedPaymentWithError = async (error) => {
  const body = clientSideFailedPaymentPayload('flywire', error, null, installmentsNumber());

  await createFailedPayment(body).then(res => res.json()).then((res) => {
    const errorMessage = res.error_message || processingErrorMessage;
    displayFlashErrorMessage(errorMessage);
  });
};

const updateFailedPaymentAndReload = async (paymentId) => {
  await updateFailedPayment(paymentId).then((response) => {
    if (response.ok) {
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  });
};

const checkoutExceptionHandler = (paymentId) => {
  displayFlashErrorMessage(processingErrorMessage);
  const retryButton = getRetryButton();
  if (retryButton) {
    retryButton.classList.remove('hidden');
  }
  updateFailedPaymentAndReload(paymentId);
};

const checkoutErrorHandler = (error) => {
  createFailedPaymentWithError(error);

  // Remove errored form to re-render by clicking Retry button
  const flywireLoader = getFlywireLoader();
  if (flywireLoader) {
    const iframes = flywireLoader.querySelectorAll('iframe');
    iframes.forEach((iframe) => {
      iframe.remove();
    });
  }

  // Enable Retry button
  showRetryButton();
};

const checkoutSuccessHandler = () => {
  const data = getFlywireData();
  if (!data) return;

  const paymentId = data.payment_id;
  const paymentInstallmentId = data.payment_installment_id;
  const callbackUrl = `payment_installments/${paymentInstallmentId}/payments/${paymentId}/payment_callback`;
  window.location = `${window.location.origin}/${bootstrapper.locale}/${callbackUrl}`;
};

const flywirePaymentElement = async (data) => {
  const sdk = await window.FlywireSDK(
    bootstrapper.flywireFrontendIntegrationKey,
  );

  const elements = await sdk.elements();

  try {
    const checkout = await elements.create('payment', {
      sessionId: data.checkout_session_id,
      displayMode: 'container', // 'full-screen' or 'container'
    });

    hideRetryButton();

    // Handle success event
    checkout.onEvent('success', () => {
      checkoutSuccessHandler();
    });

    // Handle error event
    checkout.onEvent('error', (error) => {
      checkoutErrorHandler(error);
    });

    checkout.mount('js-flywire-modal-loader'); // Mount the element to the container

    applyCssToCheckoutIframe();
  } catch {
    checkoutExceptionHandler(data.payment_id);
  }
};

export const flywireGateway = () => {
  if (flywireCheckoutEnabled()) {
    const data = getFlywireData();
    if (data) {
      flywirePaymentElement(data);
    }
  }
};

// Add event listener when DOM is ready
const initializeRetryButton = () => {
  const retryButton = getRetryButton();
  if (retryButton) {
    retryButton.addEventListener('click', (e) => {
      e.preventDefault();

      retryButton.classList.add('disabled');
      removeFlashErrorMessage();
      flywireGateway();
    });
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeRetryButton);
} else {
  initializeRetryButton();
}

export default flywireGateway;
